/* DL 2026-07-29: focus management for the mobile navigation drawer.
   Framework-independent (no Alpine dependency, no init race):
   - on open: remember the opener, move focus into the drawer
   - while open: trap Tab / Shift+Tab inside the drawer
   - on close: return focus to the opener (#mobile-navigation-opener)
   Open/close is detected by watching the drawer's `menu-opening` class,
   which the theme toggles via Alpine `$store.xMobileNav.show`. */
(() => {
  let lastOpener = null;
  let trapHandler = null;
  let wasOpen = false;

  const focusables = (root) =>
    Array.from(
      root.querySelectorAll(
        'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.getClientRects().length > 0);

  const installTrap = (drawer) => {
    trapHandler = (e) => {
      if (e.key !== 'Tab') return;
      const items = focusables(drawer);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!drawer.contains(active)) {
        e.preventDefault();
        first.focus();
        return;
      }
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', trapHandler, true);
  };

  const removeTrap = () => {
    if (trapHandler) {
      document.removeEventListener('keydown', trapHandler, true);
      trapHandler = null;
    }
  };

  /* DL 2026-07-30: the drawer is only translated off-canvas when closed, so it stays
     display:block / visibility:visible with all ~18 links still in the tab order, and it
     keeps aria-modal="true" permanently. On desktop a keyboard user tabs straight into an
     invisible menu (WCAG 2.4.3 / 2.4.7). `inert` removes the subtree from both the tab
     order and the accessibility tree in one attribute. Must be cleared BEFORE we try to
     focus into the drawer, or the focus call is swallowed. */
  const setClosedState = (drawer, closed) => {
    if (closed) {
      drawer.setAttribute('inert', '');
      drawer.setAttribute('aria-hidden', 'true');
    } else {
      drawer.removeAttribute('inert');
      drawer.removeAttribute('aria-hidden');
    }
  };

  const onOpen = (drawer) => {
    lastOpener = document.getElementById('mobile-navigation-opener') || document.activeElement;
    installTrap(drawer);
    // wait for the slide-in transition so focusables are visible
    setTimeout(() => {
      const items = focusables(drawer);
      if (items.length) items[0].focus();
    }, 350);
  };

  const onClose = () => {
    removeTrap();
    if (lastOpener && document.contains(lastOpener)) {
      lastOpener.focus();
    }
    lastOpener = null;
  };

  const watch = () => {
    const drawer = document.getElementById('mobile-navigation-drawer');
    if (!drawer) return false;
    const check = () => {
      const isOpen = drawer.classList.contains('menu-opening');
      // Clear inert before onOpen's focus call, and re-apply it only after onClose has
      // handed focus back to the opener.
      if (isOpen) setClosedState(drawer, false);
      if (isOpen && !wasOpen) onOpen(drawer);
      if (!isOpen && wasOpen) onClose();
      if (!isOpen) setClosedState(drawer, true);
      wasOpen = isOpen;
    };
    new MutationObserver(check).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    check();
    return true;
  };

  // The drawer is inside an Alpine x-if template, so it may not exist yet.
  const boot = () => {
    if (watch()) return;
    const bodyObserver = new MutationObserver(() => {
      if (watch()) bodyObserver.disconnect();
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
